/**
 * @page FlexEinsatzDetail
 * @route /flex/einsaetze/:id
 * @description Einsatz-Detail: Zusagen-Liste, Progress, Broadcast-Modal, Erinnerung.
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import ConfirmDialog from '../components/ConfirmDialog';
import BackLink from '../components/BackLink';
import { Send, Bell, Pencil, Trash2, X, Check, Clock, Mail, MessageSquare, Star } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

const STATUS_MAP = {
  draft:        { label: 'flex.einsaetze.status.draft',      color: '#9CA3AF', bg: '#F3F4F6' },
  broadcast:    { label: 'flex.einsaetze.status.broadcast',   color: '#F59E0B', bg: '#FEF3C7' },
  voll:         { label: 'flex.einsaetze.status.voll',        color: '#10B981', bg: '#D1FAE5' },
  abgeschlossen:{ label: 'flex.einsaetze.status.abgeschlossen', color: '#338edc', bg: '#EBF4FD' },
};

function StatusBadge({ status, t }) {
  const s = STATUS_MAP[status] || STATUS_MAP.draft;
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: s.bg, color: s.color }}>
      {t(s.label) || status}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatTime(t) {
  if (!t) return '';
  return String(t).substring(0, 5);
}

function formatDateTime(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function StarRating({ value, onChange, size = 18, readonly = false }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <button
          key={s}
          type="button"
          disabled={readonly}
          onClick={() => onChange?.(s === value ? 0 : s)}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}
        >
          <Star
            size={size}
            fill={s <= value ? '#F59E0B' : 'none'}
            className={s <= value ? 'text-amber-400' : 'text-[var(--text-muted,#9CA3AF)]'}
          />
        </button>
      ))}
    </div>
  );
}

export default function FlexEinsatzDetail() {
  const { id } = useParams();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [einsatz, setEinsatz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [ratings, setRatings] = useState({});   // { zusageId: { stars, kommentar } }
  const [savingRating, setSavingRating] = useState(null);

  const fetchDetail = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/flex/einsaetze/${id}`, { credentials: 'include' });
      if (res.ok) {
        setEinsatz(await res.json());
        setError(null);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || `Fehler ${res.status}`);
      }
    } catch (err) {
      setError('Netzwerkfehler');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleDelete = () => setConfirmDelete(true);
  const executeDelete = async () => {
    setConfirmDelete(false);
    const res = await fetch(`${API}/api/flex/einsaetze/${id}`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) navigate('/flex/einsaetze');
  };

  const handleRemind = async () => {
    setActionMsg('');
    const res = await fetch(`${API}/api/flex/einsaetze/${id}/remind`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channels: ['email', 'sms'] }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setActionMsg(`${data.erinnerungen_gesendet || 0} E-Mails + ${data.sms_gesendet || 0} SMS`);
      fetchDetail();
    } else setActionMsg(data.error || 'Fehler');
  };

  const handleStatusChange = async (newStatus) => {
    await fetch(`${API}/api/flex/einsaetze/${id}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    fetchDetail();
  };

  const saveRating = async (zusageId) => {
    const r = ratings[zusageId];
    if (!r?.stars) return;
    setSavingRating(zusageId);
    try {
      const body = { bewertung: r.stars, kommentar: r.kommentar || '' };
      // #949 — Stunden-Erfassung optional mitschicken (wenn Von+Bis ausgefüllt)
      if (r.von && r.bis) {
        body.arbeitszeit_von = r.von;
        body.arbeitszeit_bis = r.bis;
        body.pause_minuten = r.pause === '' || r.pause == null ? 0 : parseInt(r.pause, 10) || 0;
      }
      const res = await fetch(`${API}/api/flex/einsaetze/${id}/zusagen/${zusageId}/bewertung`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setRatings(prev => { const n = { ...prev }; delete n[zusageId]; return n; });
        fetchDetail();
      } else {
        const d = await res.json().catch(() => ({}));
        setActionMsg(d.error || 'Fehler');
      }
    } catch { setActionMsg('Netzwerkfehler'); }
    finally { setSavingRating(null); }
  };

  // #949 — Live Netto-Stunden-Berechnung (Frontend-Vorschau)
  const calcNetto = (von, bis, pause) => {
    if (!von || !bis) return null;
    const m = /^([01]\d|2[0-3]):[0-5]\d$/;
    if (!m.test(von) || !m.test(bis)) return null;
    const [vh, vm] = von.split(':').map(Number);
    const [bh, bm] = bis.split(':').map(Number);
    const vMin = vh * 60 + vm;
    let bMin = bh * 60 + bm;
    if (bMin <= vMin) bMin += 24 * 60;
    const p = pause === '' || pause == null ? 0 : parseInt(pause, 10) || 0;
    if (p < 0) return null;
    const netto = bMin - vMin - p;
    if (netto <= 0) return null;
    return Math.round((netto / 60) * 10) / 10;
  };

  if (loading) return <div className="p-8 text-center text-[var(--text-tertiary,#6B7280)]">...</div>;
  if (error) return (
    <div className="p-6 md:p-8">
      <BackLink to="/flex/einsaetze" label={t('flex.einsaetze.title')} className="mb-4" />
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-600">{error}</div>
    </div>
  );
  if (!einsatz) return null;

  const zusagen = einsatz.zusagen || [];
  const confirmed = zusagen.filter(z => z.status === 'zugesagt');
  const pending = zusagen.filter(z => z.status === 'ausstehend');
  const zugesagt = confirmed.length;
  const pct = einsatz.benoetigt > 0 ? Math.min(100, Math.round((zugesagt / einsatz.benoetigt) * 100)) : 0;

  return (
    <div className="p-6 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <BackLink to="/flex/einsaetze" label={t('flex.einsaetze.title')} className="mb-2" />
          <h1 className="text-xl font-semibold text-[var(--text-primary,#1A1A2E)]">{einsatz.titel}</h1>
          <p className="text-sm text-[var(--text-secondary,#4B5563)] mt-1">
            {formatDate(einsatz.datum_von)}{einsatz.datum_bis ? ` – ${formatDate(einsatz.datum_bis)}` : ''}, {formatTime(einsatz.uhrzeit_von)} – {formatTime(einsatz.uhrzeit_bis)}
          </p>
        </div>
        <StatusBadge status={einsatz.status} t={t} />
      </div>

      {/* Progress Card */}
      <div className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-6">
        <p className="text-sm font-medium text-[var(--text-primary,#1A1A2E)] mb-3">
          {t('flex.einsaetze.slots').replace('{confirmed}', zugesagt).replace('{needed}', einsatz.benoetigt)}
        </p>
        <div className="w-full h-3 rounded-full bg-[var(--bg-inset,#F3F4F6)] overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, backgroundColor: pct >= 100 ? '#10B981' : '#338edc' }}
          />
        </div>
        <p className="text-xs text-[var(--text-secondary,#4B5563)] mt-2">
          {zugesagt >= einsatz.benoetigt
            ? t('flex.einsaetze.full')
            : t('flex.einsaetze.slotsFree').replace('{free}', einsatz.benoetigt - zugesagt)
          }
        </p>

        {/* Info rows */}
        {(einsatz.aufgabe || einsatz.zusatz_info) && (
          <div className="mt-4 pt-4 border-t border-[var(--border-table,#F3F4F6)] grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            {einsatz.aufgabe && (
              <div>
                <span className="text-[var(--text-tertiary,#6B7280)] text-xs">{t('flex.einsaetze.field.aufgabe')}</span>
                <p className="text-[var(--text-primary,#1A1A2E)]">{einsatz.aufgabe}</p>
              </div>
            )}
            {einsatz.zusatz_info && (
              <div>
                <span className="text-[var(--text-tertiary,#6B7280)] text-xs">{t('flex.einsaetze.field.zusatzInfo')}</span>
                <p className="text-[var(--text-primary,#1A1A2E)]">{einsatz.zusatz_info}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Zusagen-Liste */}
      {confirmed.length > 0 && (
        <div className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-6">
          <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-4 uppercase tracking-wider">{t('flex.einsaetze.confirmed')}</h3>
          <div className="space-y-3">
            {confirmed.map(z => {
              const isAbgeschlossen = einsatz.status === 'abgeschlossen';
              const hasRating = z.bewertung != null;
              const pendingRating = ratings[z.id];
              return (
                <div key={z.id} className="py-3 border-b border-[var(--border-table,#F3F4F6)] last:border-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Check size={16} className="text-[#10B981]" />
                      <span className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">{z.vorname} {z.nachname}</span>
                      {z.telefon && <span className="text-xs text-[var(--text-tertiary,#6B7280)]">{z.telefon}</span>}
                    </div>
                    <span className="text-xs text-[var(--text-tertiary,#6B7280)]">{t('flex.einsaetze.confirmedAt')} {formatDateTime(z.zugesagt_am)}</span>
                  </div>
                  {/* Rating */}
                  {isAbgeschlossen && (
                    <div className="mt-2 ml-7">
                      {hasRating ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <StarRating value={z.bewertung} readonly size={16} />
                            <span className="text-xs text-[var(--text-secondary,#4B5563)]">{z.bewertung}/5</span>
                            {z.bewertung_kommentar && (
                              <span className="text-xs text-[var(--text-tertiary,#6B7280)] italic ml-1">"{z.bewertung_kommentar}"</span>
                            )}
                            {z.bewertet_von_name && (
                              <span className="text-[10px] text-[var(--text-muted,#9CA3AF)]">— {z.bewertet_von_name}</span>
                            )}
                          </div>
                          {/* #949 — Erfasste Stunden-Anzeige */}
                          {z.tatsaechliche_stunden != null && (
                            <div className="flex items-center gap-2 flex-wrap text-xs text-[var(--text-tertiary,#6B7280)]">
                              <Clock size={12} />
                              <span>
                                {formatTime(z.arbeitszeit_von)}–{formatTime(z.arbeitszeit_bis)}
                                {z.pause_minuten > 0 ? ` · ${t('flex.rating.pauseShort')} ${z.pause_minuten} min` : ''}
                                <span className="font-medium text-[var(--text-secondary,#4B5563)]"> · {z.tatsaechliche_stunden} h {t('flex.rating.netto')}</span>
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <StarRating
                              value={pendingRating?.stars || 0}
                              onChange={s => setRatings(prev => ({
                                ...prev,
                                [z.id]: {
                                  ...prev[z.id],
                                  stars: s,
                                  // #949 — Vorbelegung mit Soll-Zeit aus Einsatz
                                  von: prev[z.id]?.von ?? formatTime(einsatz.uhrzeit_von),
                                  bis: prev[z.id]?.bis ?? formatTime(einsatz.uhrzeit_bis),
                                  pause: prev[z.id]?.pause ?? '',
                                },
                              }))}
                              size={18}
                            />
                            {pendingRating?.stars > 0 && (
                              <span className="text-xs text-[var(--text-secondary,#4B5563)]">{pendingRating.stars}/5</span>
                            )}
                          </div>
                          {pendingRating?.stars > 0 && (
                            <div className="space-y-2">
                              {/* #949 — Stunden-Erfassung Von/Bis/Pause */}
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="flex items-center gap-1.5">
                                  <label className="text-[11px] text-[var(--text-tertiary,#6B7280)] w-8">{t('flex.rating.from')}</label>
                                  <input
                                    type="time"
                                    value={pendingRating?.von || ''}
                                    onChange={e => setRatings(prev => ({ ...prev, [z.id]: { ...prev[z.id], von: e.target.value } }))}
                                    className="px-2 py-1 border border-[var(--border-default,#E5E7EB)] rounded-lg text-xs bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)] w-[88px]"
                                  />
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <label className="text-[11px] text-[var(--text-tertiary,#6B7280)] w-6">{t('flex.rating.to')}</label>
                                  <input
                                    type="time"
                                    value={pendingRating?.bis || ''}
                                    onChange={e => setRatings(prev => ({ ...prev, [z.id]: { ...prev[z.id], bis: e.target.value } }))}
                                    className="px-2 py-1 border border-[var(--border-default,#E5E7EB)] rounded-lg text-xs bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)] w-[88px]"
                                  />
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <label className="text-[11px] text-[var(--text-tertiary,#6B7280)]">{t('flex.rating.pause')}</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="720"
                                    step="5"
                                    value={pendingRating?.pause ?? ''}
                                    onChange={e => setRatings(prev => ({ ...prev, [z.id]: { ...prev[z.id], pause: e.target.value } }))}
                                    placeholder="0"
                                    className="px-2 py-1 border border-[var(--border-default,#E5E7EB)] rounded-lg text-xs bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)] w-[64px] text-right"
                                  />
                                  <span className="text-[11px] text-[var(--text-tertiary,#6B7280)]">min</span>
                                </div>
                                {(() => {
                                  const netto = calcNetto(pendingRating?.von, pendingRating?.bis, pendingRating?.pause);
                                  return netto != null ? (
                                    <span className="text-xs font-medium text-[var(--text-secondary,#4B5563)] inline-flex items-center gap-1">
                                      <Clock size={12} /> {netto} h {t('flex.rating.netto')}
                                    </span>
                                  ) : (pendingRating?.von && pendingRating?.bis) ? (
                                    <span className="text-xs text-rose-600">{t('flex.rating.invalidTime')}</span>
                                  ) : null;
                                })()}
                              </div>
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={pendingRating?.kommentar || ''}
                                  onChange={e => setRatings(prev => ({ ...prev, [z.id]: { ...prev[z.id], kommentar: e.target.value } }))}
                                  placeholder={t('flex.rating.commentPlaceholder')}
                                  className="flex-1 px-2 py-1.5 border border-[var(--border-default,#E5E7EB)] rounded-lg text-xs bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]"
                                />
                                <button
                                  onClick={() => saveRating(z.id)}
                                  disabled={savingRating === z.id}
                                  className="px-3 py-1.5 rounded-lg bg-[#338edc] text-white text-xs font-medium hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors"
                                >
                                  {savingRating === z.id ? '...' : t('flex.rating.save')}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {/* Show open slots */}
            {zugesagt < einsatz.benoetigt && Array.from({ length: einsatz.benoetigt - zugesagt }).map((_, i) => (
              <div key={`open-${i}`} className="flex items-center gap-3 py-2 opacity-40">
                <Clock size={16} className="text-[var(--text-tertiary,#6B7280)]" />
                <span className="text-sm text-[var(--text-tertiary,#6B7280)]">({t('flex.einsaetze.slotOpen')})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending */}
      {pending.length > 0 && (
        <div className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-6">
          <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-4 uppercase tracking-wider">{t('flex.einsaetze.pending')}</h3>
          <div className="space-y-3">
            {pending.map(z => (
              <div key={z.id} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <Clock size={16} className="text-[#F59E0B]" />
                  <span className="text-sm text-[var(--text-primary,#1A1A2E)]">{z.vorname} {z.nachname}</span>
                  {z.telefon && <span className="text-xs text-[var(--text-tertiary,#6B7280)]">{z.telefon}</span>}
                </div>
                <div className="flex items-center gap-2 text-xs text-[var(--text-tertiary,#6B7280)]">
                  {z.email_gesendet && <Mail size={12} />}
                  {z.sms_gesendet && <MessageSquare size={12} />}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Broadcast info */}
      {einsatz.broadcast_at && (
        <div className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-6">
          <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-2 uppercase tracking-wider">Broadcast</h3>
          <p className="text-sm text-[var(--text-secondary,#4B5563)]">
            {zusagen.length} {t('flex.einsaetze.invited')} — {formatDateTime(einsatz.broadcast_at)}
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap items-center gap-3">
        {einsatz.status === 'draft' && (
          <button
            onClick={() => setShowBroadcast(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold transition-colors"
          >
            <Send size={16} /> {t('flex.einsaetze.broadcast')}
          </button>
        )}
        {(einsatz.status === 'broadcast' || einsatz.status === 'voll') && pending.length > 0 && (
          <button
            onClick={handleRemind}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold transition-colors"
          >
            <Bell size={16} /> {t('flex.einsaetze.remind')}
          </button>
        )}
        {einsatz.status === 'broadcast' && (
          <button
            onClick={() => setShowBroadcast(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] border border-[var(--border-input,#E5E7EB)] text-sm font-medium text-[var(--text-primary,#1A1A2E)] hover:bg-[var(--bg-card-hover,#F9FAFB)] transition-colors"
          >
            <Send size={16} /> {t('flex.einsaetze.broadcastMore')}
          </button>
        )}
        {(einsatz.status === 'voll' || einsatz.status === 'broadcast') && (
          <button
            onClick={() => handleStatusChange('abgeschlossen')}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] border border-[var(--border-input,#E5E7EB)] text-sm font-medium text-[var(--text-primary,#1A1A2E)] hover:bg-[var(--bg-card-hover,#F9FAFB)] transition-colors"
          >
            <Check size={16} /> {t('flex.einsaetze.complete')}
          </button>
        )}
        {einsatz.status === 'draft' && (
          <button
            onClick={handleDelete}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
          >
            <Trash2 size={16} /> {t('common.delete') || 'Löschen'}
          </button>
        )}
      </div>

      {actionMsg && <p className="text-sm text-[var(--text-secondary,#4B5563)]">{actionMsg}</p>}

      {/* Broadcast Modal */}
      {showBroadcast && (
        <BroadcastModal
          t={t}
          einsatzId={id}
          onClose={() => setShowBroadcast(false)}
          onDone={() => { setShowBroadcast(false); fetchDetail(); }}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        onConfirm={executeDelete}
        onCancel={() => setConfirmDelete(false)}
        title={t('flex.einsaetze.deleteTitle', 'Einsatz loeschen')}
        message={t('flex.einsaetze.confirmDelete', 'Diesen Einsatz wirklich loeschen?')}
        confirmLabel={t('common.delete', 'Loeschen')}
        cancelLabel={t('common.cancel', 'Abbrechen')}
        variant="danger"
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  BROADCAST MODAL
// ═══════════════════════════════════════════════════════════════════

function BroadcastModal({ t, einsatzId, onClose, onDone }) {
  const [pool, setPool] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [channels, setChannels] = useState({ email: true, sms: true });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/flex/bewerber?status=aktiv&limit=500`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const members = data.bewerber || [];
          setPool(members);
          setSelected(new Set(members.map(m => m.id)));
        }
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  const toggleAll = () => {
    if (selected.size === pool.length) setSelected(new Set());
    else setSelected(new Set(pool.map(m => m.id)));
  };

  const toggleOne = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setSelected(s);
  };

  // #1847: Fußzeile zählt ehrlich je Kanal — SMS nur an verifizierte Nummern (Server-Regel)
  const selectedMembers = pool.filter(m => selected.has(m.id));
  const withoutPhone = selectedMembers.filter(m => !m.telefon);
  const smsReady = selectedMembers.filter(m => m.telefon && m.telefon_verifiziert);
  const unverifiedSelected = selectedMembers.filter(m => m.telefon && !m.telefon_verifiziert);
  const emailCount = channels.email ? selectedMembers.filter(m => m.email).length : 0;
  const smsCount = channels.sms ? smsReady.length : 0;

  const [verifyState, setVerifyState] = useState({});
  const sendVerifySms = async (id) => {
    setVerifyState(s => ({ ...s, [id]: 'sending' }));
    try {
      const res = await fetch(`${API}/api/flex/bewerber/${id}/verify-phone`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setVerifyState(s => ({ ...s, [id]: 'sent' }));
      else setVerifyState(s => ({ ...s, [id]: data.error === 'COOLDOWN' ? 'cooldown' : 'error' }));
    } catch { setVerifyState(s => ({ ...s, [id]: 'error' })); }
  };

  const send = async () => {
    setSending(true);
    try {
      const ch = [];
      if (channels.email) ch.push('email');
      if (channels.sms) ch.push('sms');
      const res = await fetch(`${API}/api/flex/einsaetze/${einsatzId}/broadcast`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bewerber_ids: [...selected], channels: ch }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setResult(data);
        setTimeout(() => onDone(), 2000);
      } else {
        setResult({ error: data.error || 'Fehler' });
      }
    } catch { setResult({ error: 'Netzwerkfehler' }); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl shadow-sm w-full max-w-xl mx-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 pb-4 border-b border-[var(--border-card,#E5E7EB)]">
          <h2 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)]">{t('flex.einsaetze.broadcast')}</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-[var(--bg-card-hover,#F9FAFB)]"><X size={18} className="text-[var(--text-tertiary,#6B7280)]" /></button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Channel toggles */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-[var(--text-primary,#1A1A2E)]">
              <input type="checkbox" checked={channels.email} onChange={e => setChannels(c => ({ ...c, email: e.target.checked }))} className="rounded" />
              {t('flex.einsaetze.channels.email')}
            </label>
            <label className="flex items-center gap-2 text-sm text-[var(--text-primary,#1A1A2E)]">
              <input type="checkbox" checked={channels.sms} onChange={e => setChannels(c => ({ ...c, sms: e.target.checked }))} className="rounded" />
              {t('flex.einsaetze.channels.sms')}
            </label>
          </div>

          {/* Select all */}
          <label className="flex items-center gap-2 text-sm font-medium text-[var(--text-primary,#1A1A2E)] cursor-pointer">
            <input type="checkbox" checked={selected.size === pool.length && pool.length > 0} onChange={toggleAll} className="rounded" />
            {t('flex.einsaetze.selectAll')} ({pool.length} {t('flex.einsaetze.activeMembers')})
          </label>

          {/* Member list */}
          <div className="border border-[var(--border-card,#E5E7EB)] rounded-xl max-h-[240px] overflow-y-auto divide-y divide-[var(--border-table,#F3F4F6)]">
            {loading ? (
              <div className="p-4 text-center text-[var(--text-tertiary,#6B7280)] text-sm">...</div>
            ) : pool.map(m => {
              const unverified = m.telefon && !m.telefon_verifiziert;
              const vState = verifyState[m.id];
              return (
                <div key={m.id} className="px-4 py-2.5 hover:bg-[var(--bg-card-hover,#FAFAFE)]">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggleOne(m.id)} className="rounded" />
                    <span className="text-sm text-[var(--text-primary,#1A1A2E)] flex-1">{m.vorname} {m.nachname}</span>
                    <span className="text-xs text-[var(--text-tertiary,#6B7280)]">{m.telefon || '—'}</span>
                    <span className="text-xs text-[var(--text-tertiary,#6B7280)] truncate max-w-[140px]">{m.email}</span>
                  </label>
                  {unverified && (
                    <div className="flex items-center gap-2 mt-1 ml-7">
                      <span className="text-xs text-[#F59E0B]">
                        {t('flex.verify.smsAfterVerify') || 'SMS erst nach Verifizierung'}
                      </span>
                      {vState === 'sent' ? (
                        <span className="text-xs text-green-600">{t('flex.verify.codeSent') || 'Verifizierungs-SMS gesendet'}</span>
                      ) : vState === 'cooldown' ? (
                        <span className="text-xs text-[var(--text-tertiary,#6B7280)]">{t('flex.verify.cooldown') || 'Bitte kurz warten'}</span>
                      ) : vState === 'error' ? (
                        <span className="text-xs text-red-500">{t('flex.verify.sendError') || 'Senden fehlgeschlagen'}</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => sendVerifySms(m.id)}
                          disabled={vState === 'sending'}
                          className="text-xs text-[#338edc] hover:underline disabled:opacity-50"
                        >
                          {vState === 'sending' ? '...' : (t('flex.verify.sendVerifySms') || 'Verifizierungs-SMS senden')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Summary */}
          {channels.sms && withoutPhone.length > 0 && (
            <p className="text-xs text-[#F59E0B]">
              {withoutPhone.length} {t('flex.einsaetze.noPhone')}
            </p>
          )}
          {channels.sms && unverifiedSelected.length > 0 && (
            <p className="text-xs text-[#F59E0B]">
              {unverifiedSelected.length} {t('flex.verify.unverifiedNoSms') || 'ohne verifizierte Nummer — erhalten keine SMS'}
            </p>
          )}
          <p className="text-sm text-[var(--text-secondary,#4B5563)]">
            {emailCount} {t('flex.einsaetze.channels.email')} + {smsCount} {t('flex.einsaetze.channels.sms')} {t('flex.einsaetze.willBeSent')}
          </p>

          {result && (
            <div className={`text-sm p-3 rounded-lg ${result.error ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>
              {result.error || `${result.emails_sent} E-Mails + ${result.sms_sent} SMS ${t('flex.einsaetze.sent')}${result.sms_blockiert_unverifiziert > 0 ? ` — ${result.sms_blockiert_unverifiziert} ${t('flex.verify.unverifiedNoSms') || 'ohne verifizierte Nummer — erhalten keine SMS'}` : ''}`}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 pt-4 border-t border-[var(--border-card,#E5E7EB)]">
          <button onClick={onClose} className="px-4 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] text-sm font-medium text-[var(--text-primary,#1A1A2E)] hover:bg-[var(--bg-card-hover,#F9FAFB)]">
            {t('common.cancel') || 'Abbrechen'}
          </button>
          <button
            onClick={send}
            disabled={sending || selected.size === 0 || (!channels.email && !channels.sms) || emailCount + smsCount === 0}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50 transition-colors"
          >
            <Send size={16} />
            {sending ? '...' : t('flex.einsaetze.sendNow')}
          </button>
        </div>
      </div>
    </div>
  );
}
